/**
 * Voice2Evidence™ Patient Advisory Brief (PAB) PDF.
 *
 * Live-engagement discussion guide for an upcoming patient advisory board.
 * Renders the 5 V2E parent themes (A-E) as ordered chapters; each chapter
 * lists 8 questions (3 Confirm · 3 Gap-probe · 2 Tension) with their
 * sub-theme probes as small grey captions.
 *
 * Brand chrome matches ReportPdf.jsx — same wordmark, rust accent line,
 * footer disclosure. Illustrative-engagement notice is suppressed when the
 * engagement is `kind === "live"`.
 */
import {
  Document, Page, Text, View, StyleSheet, Svg, Line,
} from "@react-pdf/renderer";

const RUST = "#C84C21";
const INK  = "#0F1825";
const MUTE = "#5C6470";
const SOFT = "#A09680";
const CREAM = "#FAF7F1";

// V2E theme accent palette — kept identical to the frontend V2E_THEMES.
const THEME_ACCENTS = {
  A: "#3F6D55",
  B: "#3D5A8C",
  C: "#C84C21",
  D: "#3A8C9A",
  E: "#9A6B1A",
};

// PAB question-type chip palette.
const TYPE_PALETTE = {
  "Confirm":   { bg: "#E8F0EA", fg: "#2C5C42", border: "#B6D2BC" },
  "Gap-probe": { bg: "#FDF1E6", fg: "#8C3A12", border: "#E8C19A" },
  "Tension":   { bg: "#F1ECF4", fg: "#5B3C7A", border: "#CCBADB" },
};

// KOL question-type chip palette — mirrors the on-screen tab palette so
// the printed brief reads as an extension of the live workspace.
const KOL_TYPE_PALETTE = {
  "Positioning": { bg: "#E4EEF7", fg: "#22456A", border: "#B7CFE3" },
  "Adoption":    { bg: "#FDF1E6", fg: "#8C3A12", border: "#E8C19A" },
  "Advocacy":    { bg: "#EDE9F4", fg: "#4E378A", border: "#C9BFE0" },
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 56,
    paddingBottom: 64,
    paddingHorizontal: 56,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: INK,
    lineHeight: 1.5,
  },
  brandRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  brandLeft:  { flexDirection: "column" },
  brandRight: { flexDirection: "column", alignItems: "flex-end" },
  wordmark:   { fontSize: 11, color: RUST, letterSpacing: 3, fontFamily: "Helvetica-Bold" },
  tmMark:     { fontSize: 6.5, color: RUST, fontFamily: "Helvetica-Bold", marginLeft: 1, paddingTop: 1 },
  tagline:    { fontSize: 8, color: SOFT, letterSpacing: 2, marginTop: 4, marginLeft: 16 },
  reportKind: { fontSize: 8, color: MUTE, letterSpacing: 2, textTransform: "uppercase" },
  reportRef:  { fontSize: 8, color: SOFT, marginTop: 2 },

  eyebrow:    { fontSize: 9, color: RUST, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 8 },
  title:      { fontSize: 24, color: INK, fontFamily: "Times-Roman", lineHeight: 1.15, marginBottom: 10 },
  subtitle:   { fontSize: 11, color: MUTE, lineHeight: 1.55, marginBottom: 22, maxWidth: 460 },

  metaGrid:   { marginTop: 14, marginBottom: 22, flexDirection: "row", flexWrap: "wrap" },
  metaCell:   { width: "50%", marginBottom: 14, paddingRight: 12 },
  metaLabel:  { fontSize: 7.5, color: SOFT, letterSpacing: 1.8, textTransform: "uppercase", marginBottom: 3 },
  metaValue:  { fontSize: 11, color: INK },

  // Theme chapter band
  themeBand: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginTop: 16,
    marginBottom: 10,
  },
  themeKey:   { fontSize: 10, color: "#FFFFFF", letterSpacing: 2, fontFamily: "Helvetica-Bold" },
  themeDot:   { color: "#FFFFFF", marginHorizontal: 8 },
  themeLabel: { fontSize: 11, color: "#FFFFFF", fontFamily: "Times-Roman" },

  // Question row
  qRow: { marginBottom: 12, flexDirection: "row", alignItems: "flex-start" },
  qNum: { width: 22, fontSize: 9, color: MUTE, paddingTop: 2 },
  qBody: { flex: 1 },
  qTypeChip: {
    fontSize: 7.5,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    paddingHorizontal: 5,
    paddingVertical: 2,
    marginRight: 6,
    borderWidth: 1,
  },
  qProbeCaption: {
    fontSize: 7.5,
    color: SOFT,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    marginTop: 4,
  },
  qText: {
    fontSize: 10.5,
    color: INK,
    lineHeight: 1.5,
    marginTop: 5,
  },

  emptyMute: { fontSize: 10, color: MUTE, lineHeight: 1.5, marginTop: 6 },

  // Footer
  footer: {
    position: "absolute",
    bottom: 28,
    left: 56,
    right: 56,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#E8E1D2",
  },
  footerLeft:  { fontSize: 7, color: SOFT, letterSpacing: 1.4 },
  footerRight: { fontSize: 7, color: SOFT, letterSpacing: 1.4 },

  disclosure:     { marginTop: 18, padding: 10, backgroundColor: "#FDF7E6", borderWidth: 1, borderColor: "#E8D08A" },
  disclosureLbl:  { fontSize: 7.5, color: "#9A6B1A", letterSpacing: 1.8, marginBottom: 4 },
  disclosureBody: { fontSize: 9, color: "#5C4520", lineHeight: 1.5 },

  liveBadge:     { marginTop: 18, padding: 10, backgroundColor: "#E8F0EA", borderWidth: 1, borderColor: "#B6D2BC" },
  liveBadgeLbl:  { fontSize: 7.5, color: "#2C5C42", letterSpacing: 1.8, marginBottom: 4 },
  liveBadgeBody: { fontSize: 9, color: "#1F4530", lineHeight: 1.5 },

  // Cover: orientation card
  orientCard: {
    marginTop: 18,
    padding: 14,
    backgroundColor: CREAM,
    borderLeftWidth: 3,
    borderLeftColor: RUST,
  },
  orientTitle: { fontSize: 10, color: RUST, letterSpacing: 1.8, textTransform: "uppercase", marginBottom: 6 },
  orientBody:  { fontSize: 10, color: INK, lineHeight: 1.55, marginBottom: 4 },
});

function AccentLine() {
  return (
    <Svg width="32" height="2" style={{ marginTop: 10, marginBottom: 22 }}>
      <Line x1="0" y1="1" x2="32" y2="1" stroke={RUST} strokeWidth="2" />
    </Svg>
  );
}

function BrandHeader({ reportKind, reportRef }) {
  return (
    <View fixed>
      <View style={styles.brandRow}>
        <View style={styles.brandLeft}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Svg width="10" height="10" style={{ marginRight: 6 }}>
              <Line x1="0" y1="5" x2="10" y2="5" stroke={RUST} strokeWidth="3" />
            </Svg>
            <Text style={styles.wordmark}>VOICE2EVIDENCE</Text>
            <Text style={styles.tmMark}>TM</Text>
          </View>
          <Text style={styles.tagline}>by MarksMan Healthcare</Text>
        </View>
        <View style={styles.brandRight}>
          <Text style={styles.reportKind}>{reportKind}</Text>
          {reportRef ? <Text style={styles.reportRef}>{reportRef}</Text> : null}
        </View>
      </View>
      <AccentLine />
    </View>
  );
}

function PageFooter({ reportKind, eng }) {
  return (
    <View style={styles.footer} fixed>
      <Text style={styles.footerLeft}>
        Voice2Evidence · {reportKind} · {eng?.brand || ""} · {eng?.client_label || ""}
      </Text>
      <Text
        style={styles.footerRight}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  );
}

function MetaCell({ label, value }) {
  return (
    <View style={styles.metaCell}>
      <Text style={styles.metaLabel}>{label}</Text>
      <Text style={styles.metaValue}>{value || "—"}</Text>
    </View>
  );
}

function QuestionRow({ n, q }) {
  const palette = TYPE_PALETTE[q.type] || TYPE_PALETTE["Confirm"];
  const probes = (q.probes || []).join(" · ");
  return (
    <View style={styles.qRow} wrap={false}>
      <Text style={styles.qNum}>{String(n).padStart(2, "0")}</Text>
      <View style={styles.qBody}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text
            style={{
              ...styles.qTypeChip,
              color: palette.fg,
              backgroundColor: palette.bg,
              borderColor: palette.border,
            }}
          >
            {q.type}
          </Text>
          {probes ? (
            <Text style={{ fontSize: 7.5, color: SOFT, letterSpacing: 1.4, textTransform: "uppercase" }}>
              Probes · {probes}
            </Text>
          ) : null}
        </View>
        <Text style={styles.qText}>{q.question}</Text>
      </View>
    </View>
  );
}

/* ============================================================
 * PATIENT ADVISORY BRIEF (PAB) REPORT
 * ============================================================ */
export function PatientAdvisoryBriefReport({ eng, pabQuestions = {}, themes = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const reportKind = "Patient Advisory Brief";
  const reportRef  = `Ref · ${eng?.brand || ""} · ${today}`;
  const isLive = eng?.kind === "live";

  // Themes are passed in (the parent component owns the V2E framework
  // constant). Filter to themes that actually have questions.
  const populated = themes.filter((t) => (pabQuestions[t.key] || []).length > 0);
  const totalQuestions = populated.reduce((acc, t) => acc + (pabQuestions[t.key] || []).length, 0);

  return (
    <Document title={`Voice2Evidence — ${reportKind} — ${eng?.brand || ""}`}>
      {/* Cover page */}
      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <Text style={styles.eyebrow}>PATIENT ADVISORY BRIEF</Text>
        <Text style={styles.title}>Live discussion guide — patient advisory board.</Text>
        <Text style={styles.subtitle}>
          Triangulated desk evidence translated into a moderator-ready discussion guide.
          Every question traces back to a V2E sub-theme finding so the live conversation
          either confirms, closes, or resolves the evidence base.
        </Text>

        <View style={styles.metaGrid}>
          <MetaCell label="Client"             value={eng?.client_label} />
          <MetaCell label="Asset"              value={`${eng?.brand || ""}${eng?.asset ? ` (${eng.asset})` : ""}`} />
          <MetaCell label="Indication"         value={eng?.indication} />
          <MetaCell label="Lifecycle phase"    value={eng?.lifecycle_phase} />
          <MetaCell label="Lead methodologist" value={eng?.lead_methodologist} />
          <MetaCell label="Prepared on"        value={today} />
          <MetaCell label="V2E themes covered" value={`${populated.length} of ${themes.length}`} />
          <MetaCell label="Questions"          value={`${totalQuestions} total`} />
        </View>

        <View style={styles.orientCard}>
          <Text style={styles.orientTitle}>How to use this brief</Text>
          <Text style={styles.orientBody}>
            · Confirm questions validate strong desk findings with patients in the room.
          </Text>
          <Text style={styles.orientBody}>
            · Gap-probe questions close named evidence gaps with primary patient input.
          </Text>
          <Text style={styles.orientBody}>
            · Tension questions surface trade-offs where the desk evidence is split.
          </Text>
          <Text style={[styles.orientBody, { marginTop: 6 }]}>
            Probe chips (e.g. C2 · D1) trace each question to the V2E sub-theme finding it tests.
          </Text>
        </View>

        {isLive ? (
          <View style={styles.liveBadge}>
            <Text style={styles.liveBadgeLbl}>LIVE ENGAGEMENT</Text>
            <Text style={styles.liveBadgeBody}>
              This brief is generated from a live client engagement and the evidence base
              triangulated for it. It is intended for the named client&apos;s moderator team
              and is confidential to that engagement.
            </Text>
          </View>
        ) : (
          <View style={styles.disclosure}>
            <Text style={styles.disclosureLbl}>ILLUSTRATIVE ENGAGEMENT</Text>
            <Text style={styles.disclosureBody}>
              This simulated case study is for demonstration only. It is not based on a
              live engagement, client data, confidential information, or proprietary
              information from the asset owner.
            </Text>
          </View>
        )}

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>

      {/* One page per V2E theme — easier to print and hand to a moderator chapter-by-chapter */}
      {populated.length === 0 ? (
        <Page size="A4" style={styles.page}>
          <BrandHeader reportKind={reportKind} reportRef={reportRef} />
          <Text style={styles.emptyMute}>No PAB questions yet. Run triangulation to generate the brief.</Text>
          <PageFooter reportKind={reportKind} eng={eng} />
        </Page>
      ) : (
        populated.map((t) => {
          const questions = pabQuestions[t.key] || [];
          const accent = THEME_ACCENTS[t.key] || RUST;
          return (
            <Page key={t.key} size="A4" style={styles.page}>
              <BrandHeader reportKind={reportKind} reportRef={reportRef} />

              <View style={{ ...styles.themeBand, backgroundColor: accent }}>
                <Text style={styles.themeKey}>{t.key}</Text>
                <Text style={styles.themeDot}>·</Text>
                <Text style={styles.themeLabel}>{t.label}</Text>
              </View>

              {questions.map((q, i) => (
                <QuestionRow key={q.id || i} n={i + 1} q={q} />
              ))}

              <PageFooter reportKind={reportKind} eng={eng} />
            </Page>
          );
        })
      )}
    </Document>
  );
}


/* ============================================================
 * KOL ADVISORY BRIEF REPORT
 *
 * Pre-launch physician board discussion guide. Identical brand + layout
 * to the PAB PDF so the two deliverables read as siblings: same cover
 * chrome, same theme-band chapter breaks, same footer. Only the tag
 * palette + orientation copy diverge because the audience does.
 *
 * Every question carries one of three tags — Positioning · Adoption ·
 * Advocacy — each mapped to a real pharma pre-launch decision, so a
 * Medical Affairs / Commercial reader can plan the room straight from
 * the printed guide.
 * ============================================================ */
function KolQuestionRow({ n, q }) {
  const palette = KOL_TYPE_PALETTE[q.type] || KOL_TYPE_PALETTE["Positioning"];
  const probes = (q.probes || []).join(" · ");
  return (
    <View style={styles.qRow} wrap={false}>
      <Text style={styles.qNum}>{String(n).padStart(2, "0")}</Text>
      <View style={styles.qBody}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text
            style={{
              ...styles.qTypeChip,
              color: palette.fg,
              backgroundColor: palette.bg,
              borderColor: palette.border,
            }}
          >
            {q.type}
          </Text>
          {probes ? (
            <Text style={{ fontSize: 7.5, color: SOFT, letterSpacing: 1.4, textTransform: "uppercase" }}>
              Probes · {probes}
            </Text>
          ) : null}
        </View>
        <Text style={styles.qText}>{q.question}</Text>
      </View>
    </View>
  );
}


export function KolAdvisoryBriefReport({ eng, kolQuestions = {}, themes = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const reportKind = "KOL Advisory Brief";
  const reportRef  = `Ref · ${eng?.brand || ""} · ${today}`;
  const isLive = eng?.kind === "live";

  const populated = themes.filter((t) => (kolQuestions[t.key] || []).length > 0);
  const totalQuestions = populated.reduce((acc, t) => acc + (kolQuestions[t.key] || []).length, 0);

  return (
    <Document title={`Voice2Evidence — ${reportKind} — ${eng?.brand || ""}`}>
      {/* Cover page */}
      <Page size="A4" style={styles.page}>
        <BrandHeader reportKind={reportKind} reportRef={reportRef} />

        <Text style={styles.eyebrow}>KOL ADVISORY BRIEF · PRE-LAUNCH</Text>
        <Text style={styles.title}>Discussion guide — pre-launch KOL advisory board.</Text>
        <Text style={styles.subtitle}>
          Triangulated desk evidence translated into a moderator-ready discussion guide for
          a senior-KOL pre-launch board. Every question maps to one of three pharma launch
          decisions — positioning, adoption, or advocacy — and traces back to a V2E sub-theme
          finding so the live conversation directly informs the launch plan.
        </Text>

        <View style={styles.metaGrid}>
          <MetaCell label="Client"             value={eng?.client_label} />
          <MetaCell label="Asset"              value={`${eng?.brand || ""}${eng?.asset ? ` (${eng.asset})` : ""}`} />
          <MetaCell label="Indication"         value={eng?.indication} />
          <MetaCell label="Lifecycle phase"    value={eng?.lifecycle_phase} />
          <MetaCell label="Lead methodologist" value={eng?.lead_methodologist} />
          <MetaCell label="Prepared on"        value={today} />
          <MetaCell label="V2E themes covered" value={`${populated.length} of ${themes.length}`} />
          <MetaCell label="Questions"          value={`${totalQuestions} total`} />
        </View>

        <View style={styles.orientCard}>
          <Text style={styles.orientTitle}>How to use this brief</Text>
          <Text style={styles.orientBody}>
            · Positioning questions validate where the asset sits in the treatment sequence,
              which patient the KOL reaches for first, and how payers may frame value.
              Feeds launch statement · patient ID story · HTA narrative.
          </Text>
          <Text style={styles.orientBody}>
            · Adoption questions probe evidence, operational, and competitive barriers that
              block the KOL&apos;s first prescription.
              Feeds MSL narrative · publication + congress plan · sales training.
          </Text>
          <Text style={styles.orientBody}>
            · Advocacy questions surface what would make the KOL a public champion — data
              generation, educational assets, KOL-led studies, symposium roles.
              Feeds KOL engagement roadmap · real-world evidence plan.
          </Text>
          <Text style={[styles.orientBody, { marginTop: 6 }]}>
            Probe chips (e.g. C2 · D1) trace each question to the V2E sub-theme finding
            it tests. Anchored in the same evidence base as the Patient Advisory Brief.
          </Text>
        </View>

        {isLive ? (
          <View style={styles.liveBadge}>
            <Text style={styles.liveBadgeLbl}>LIVE ENGAGEMENT</Text>
            <Text style={styles.liveBadgeBody}>
              This brief is generated from a live client engagement and the evidence base
              triangulated for it. It is intended for the named client&apos;s moderator team
              and is confidential to that engagement.
            </Text>
          </View>
        ) : (
          <View style={styles.disclosure}>
            <Text style={styles.disclosureLbl}>ILLUSTRATIVE ENGAGEMENT</Text>
            <Text style={styles.disclosureBody}>
              This simulated case study is for demonstration only. It is not based on a
              live engagement, client data, confidential information, or proprietary
              information from the asset owner.
            </Text>
          </View>
        )}

        <PageFooter reportKind={reportKind} eng={eng} />
      </Page>

      {/* One page per V2E theme — easier to print and hand to a moderator chapter-by-chapter */}
      {populated.length === 0 ? (
        <Page size="A4" style={styles.page}>
          <BrandHeader reportKind={reportKind} reportRef={reportRef} />
          <Text style={styles.emptyMute}>
            No KOL questions yet. Run triangulation and generate the KOL brief to populate this document.
          </Text>
          <PageFooter reportKind={reportKind} eng={eng} />
        </Page>
      ) : (
        populated.map((t) => {
          const questions = kolQuestions[t.key] || [];
          const accent = THEME_ACCENTS[t.key] || RUST;
          return (
            <Page key={t.key} size="A4" style={styles.page}>
              <BrandHeader reportKind={reportKind} reportRef={reportRef} />

              <View style={{ ...styles.themeBand, backgroundColor: accent }}>
                <Text style={styles.themeKey}>{t.key}</Text>
                <Text style={styles.themeDot}>·</Text>
                <Text style={styles.themeLabel}>{t.label}</Text>
              </View>

              {questions.map((q, i) => (
                <KolQuestionRow key={q.id || i} n={i + 1} q={q} />
              ))}

              <PageFooter reportKind={reportKind} eng={eng} />
            </Page>
          );
        })
      )}
    </Document>
  );
}
